'use client';

import React, { useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { useMediaQuery } from '@/hooks/useMediaQuery';

interface BaseModalProps {
  isOpen: boolean;
  onCloseAction: () => void;
  title?: string;
  children: React.ReactNode;
}

const OVERLAY_BG = 'rgba(0, 0, 0, 0.5)';
const MODAL_BG = '#ffffff';
const BORDER_COLOR = '#e2e8f0'; // серый светлый
const TEXT_SECONDARY = '#718096'; // серый средний
const HOVER_BG = 'rgba(0, 0, 0, 0.05)';

const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: ${OVERLAY_BG};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  z-index: 1000;
`;

const Container = styled(motion.div)<{ mobile: boolean }>`
  background: ${MODAL_BG};
  border-radius: ${({ mobile }) => (mobile ? '12px 12px 0 0' : '12px')};
  width: 100%;
  max-width: ${({ mobile }) => (mobile ? '100%' : '560px')};
  max-height: 90vh;
  overflow-y: auto;
  position: ${({ mobile }) => (mobile ? 'fixed' : 'relative')};
  bottom: ${({ mobile }) => (mobile ? '0' : 'auto')};
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);

  @keyframes slideUp {
    from { transform: translateY(100%); }
    to   { transform: translateY(0); }
  }

  ${({ mobile }) =>
    mobile &&
    `
    animation: slideUp 0.3s ease-out;
  `}
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid ${BORDER_COLOR};
`;

const Title = styled.h3`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  color: ${TEXT_SECONDARY};
  border-radius: 50%;

  &:hover {
    background: ${HOVER_BG};
  }
`;

const Content = styled.div`
  padding: 24px;
`;

export const BaseModal: React.FC<BaseModalProps> = ({ isOpen, onCloseAction, title, children }) => {
  const isMobile = useMediaQuery('(max-width: 768px)');

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseAction();
    };
    if (isOpen) window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onCloseAction]);

  return (
    <AnimatePresence>
      {isOpen && (
        <Overlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onCloseAction}
        >
          <Container
            mobile={isMobile}
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 50, opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
          >
            <Header>
              {title && <Title>{title}</Title>}
              <CloseBtn onClick={onCloseAction}>
                <X size={20} />
              </CloseBtn>
            </Header>
            <Content>{children}</Content>
          </Container>
        </Overlay>
      )}
    </AnimatePresence>
  );
};
